import { jsx } from '@emotion/react/jsx-runtime';
import { createPolymorphicComponent } from '@nettbureau/core';
import { AnimatePresence, useIsPresent, motion } from 'framer-motion';
import { useCallback } from 'react';
import { useIsSSR } from 'react-aria';

/**
 * Base component for transitioning elements in and out. All transition components are built on top
 * of this component. You may also use this component yourself to build new custom transitions.
 */ const BaseTransition = createPolymorphicComponent((props, ref)=>{
    const { children, initial = !useIsSSR(), mounted, measuring = false, onExitComplete, ...elementProps } = props;
    return /*#__PURE__*/ jsx(AnimatePresence, {
        initial: initial,
        onExitComplete: onExitComplete,
        children: mounted && (measuring ? children : /*#__PURE__*/ jsx(BaseTransitionInner, {
            ...elementProps,
            ref: ref,
            children: children
        }))
    });
});
/**
 * An internal component that handles the actual animation of the transition. We need to split the
 * base transition into two components to make sure the `useIsPresent` hook works correctly.
 */ const BaseTransitionInner = createPolymorphicComponent((props, ref)=>{
    const { as = 'div', children, exit, onEntered, onEntering, onExited, onExiting, enter, ...elementProps } = props;
    const isPresent = useIsPresent();
    const handleAnimationStart = useCallback(()=>isPresent ? onEntering?.() : onExiting?.(), [
        isPresent,
        onEntering,
        onExiting
    ]);
    const handleAnimationComplete = useCallback(()=>isPresent ? onEntered?.() : onExited?.(), [
        isPresent,
        onEntered,
        onExited
    ]);
    const MotionComponent = motion[as];
    return /*#__PURE__*/ jsx(MotionComponent, {
        "data-transition": true,
        ...elementProps,
        ref: ref,
        animate: "visible",
        exit: "hidden",
        initial: "hidden",
        onAnimationComplete: handleAnimationComplete,
        onAnimationStart: handleAnimationStart,
        variants: {
            visible: enter,
            hidden: exit
        },
        children: children
    });
});

export { BaseTransition };
//# sourceMappingURL=base-transition.js.map
